import { ModuleRegistry } from '@ag-grid-community/core'
import { MasterDetailModule } from '@ag-grid-enterprise/master-detail'
import { Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { BasePanelComponent, LayoutContainer } from '@helios/panel-base'
import {
  RootState,
  selectFocusedAccounts,
  selectFocusedCorporateActionIssue,
  selectIsInternalUser,
} from '@helios/state-management'
import { Store } from '@ngrx/store'
import { Daterange } from '@northfork/ng-basics'
import { subDays, addDays } from 'date-fns'
import { ComponentContainer } from 'golden-layout'
import { Subject } from 'rxjs'
import { distinctUntilChanged, takeUntil, tap } from 'rxjs/operators'
import { CorporateActionsService } from './services/corporate-actions.service'
import { CorporateActionsKey } from '../panel-key'
import { ResizeDirection, ResizeHandleState } from '@helios/generic-components'
import { IceCorporateAction } from './models/ice-corporate-action'

ModuleRegistry.registerModules([MasterDetailModule])

interface CorporateActionsPanelState {
  clearwaterResizeHandleState?: ResizeHandleState
  iceResizeHandleState?: ResizeHandleState
  /**
   * @deprecated Pre-AMET0-3438 single-handle field. Read on hydration to migrate
   * legacy GoldenLayout configs into clearwaterResizeHandleState; never written.
   */
  resizeHandleState?: ResizeHandleState
}

/**
 * Hoist a pre-AMET0-3438 single-handle `resizeHandleState` into
 * `clearwaterResizeHandleState` so users with existing GoldenLayout configs do
 * not lose their saved Clearwater splitter on first reload after upgrade.
 * Returns a new state object; safe to call with undefined.
 *
 * Exported for unit testing.
 */
export function migrateLegacyResizeState(
  state: CorporateActionsPanelState | undefined,
): CorporateActionsPanelState | undefined {
  if (!state?.resizeHandleState || state.clearwaterResizeHandleState) {
    return state
  }
  return {
    ...state,
    clearwaterResizeHandleState: state.resizeHandleState,
    resizeHandleState: undefined,
  }
}

const ICE_DEFAULT_PAST_DAYS = 4
const ICE_DEFAULT_FUTURE_DAYS = 10

/**
 * Mirrors the ICE grid's original default date range (a look-back/look-ahead
 * window from today). Computed once here since the panel host now owns
 * `iceDateRange` for the lifetime of the session (AMET0-3522).
 *
 * Exported for unit testing.
 */
export function getDefaultIceDateRange(): Daterange {
  const today = new Date()
  const from = subDays(today, ICE_DEFAULT_PAST_DAYS)
  const to = addDays(today, ICE_DEFAULT_FUTURE_DAYS)
  return {
    beginDate: from.toISOString().split('T')[0],
    endDate: to.toISOString().split('T')[0],
  }
}

@Component({
  selector: 'hs-corporate-actions',
  templateUrl: './corporate-actions-panel.component.html',
  styleUrls: ['./corporate-actions-panel.component.scss'],
  exportAs: 'CorporateActionsPanel',
})
export class CorporateActionsPanelComponent
  extends BasePanelComponent<CorporateActionsPanelState>
  implements OnInit, OnDestroy
{
  panelKey = CorporateActionsKey
  selectedIssueId: string | undefined
  corporateActionIdSelected: string | null = ''
  selectedAccountId: number[] = []
  private readonly _destroyed$ = new Subject<void>()
  corporateActionLoaded = false
  userActions: string[] = []
  activeTopTab = 1
  activeBottomTab = 1
  selectedIceCorporateAction: IceCorporateAction | undefined
  iceSelectedClientIds: number[] = []
  iceDateRange: Daterange = getDefaultIceDateRange()
  iceRowData: IceCorporateAction[] = []
  isInternalUser = false
  /**
   * Latched the first time the user activates the Processing tab (PVAL-25952).
   *
   * Drives `[destroyOnHide]="!processingTabActivated"` on that one nav item, so
   * the tab is LAZY until first opened and KEEP-ALIVE afterwards. Both halves
   * matter: see the comment on the `<li [ngbNavItem]="3">` block in the template
   * for why a constant `false` there is eager (and red against the protected
   * panel spec) and why the default destroy-on-hide loses the child's retirement
   * state.
   */
  processingTabActivated = false
  readonly ResizeDirection = ResizeDirection

  constructor(
    @Inject(LayoutContainer) container: ComponentContainer,
    private readonly store: Store<RootState>,
    public corporateActionsService: CorporateActionsService,
  ) {
    super(container)
    this.state = migrateLegacyResizeState(this.state)
  }

  ngOnDestroy(): void {
    this._destroyed$.next()
    this._destroyed$.complete()
  }

  ngOnInit(): void {
    this.store
      .select(selectFocusedCorporateActionIssue)
      .pipe(takeUntil(this._destroyed$))
      .subscribe(issueId => this.setSelectedIssueId(issueId))

    this.store
      .select(selectFocusedAccounts)
      .pipe(
        takeUntil(this._destroyed$),
        distinctUntilChanged(),
        tap(selectedAccountId => {
          if (selectedAccountId) {
            this.setSelectedAccountId(selectedAccountId)
          }
        }),
      )
      .subscribe()

    this.corporateActionsService
      .corporateActionUserPolicy()
      .pipe(takeUntil(this._destroyed$))
      .subscribe(action => this.setUserActions(action))

    this.store
      .select(selectIsInternalUser)
      .pipe(takeUntil(this._destroyed$))
      .subscribe(internal => (this.isInternalUser = internal))
  }

  setSelectedCorporateActionId(id: string) {
    this.corporateActionIdSelected = id
  }

  setSelectedIssueId(issueId: string | undefined) {
    this.selectedIssueId = issueId
  }

  setSelectedAccountId(selectedAccountId: number[]) {
    this.selectedAccountId = selectedAccountId
  }

  setUserActions(actionsAllowed: string[]) {
    this.userActions = actionsAllowed
  }

  onClearwaterResizeFinished(clearwaterResizeHandleState: ResizeHandleState) {
    this.setState({
      ...this.state,
      clearwaterResizeHandleState,
    })
  }

  onIceResizeFinished(iceResizeHandleState: ResizeHandleState) {
    this.setState({
      ...this.state,
      iceResizeHandleState,
    })
  }

  onTopTabChange(tabId: number) {
    this.activeTopTab = tabId
    if (tabId === 1) {
      this.selectedIceCorporateAction = undefined
      this.activeBottomTab = 1
    } else if (tabId === 2) {
      this.activeBottomTab = 3
    } else if (tabId === 3) {
      // Corporate Actions Processing (PVAL-25952) keeps its state in
      // component-local fields on the CHILD, and that state survives a top-tab
      // switch only because latching `processingTabActivated` here flips the
      // tab's `[destroyOnHide]` to false (ngbNav destroys hidden content by
      // default). That latch — not this host — is what preserves it, so nothing
      // is lifted here.
      //
      // The state in question is the retirement record: retired rows,
      // submissionStatus, commandId, the UNCONFIRMED sentinel and
      // cellLevelErrorMessageMap. If the child were destroyed, ngOnInit would
      // re-run, the grid would come back as one blank row with Submit re-enabled
      // and no in-grid record that the previous commands were queued, and the
      // user would re-POST a duplicate financial corporate action. So if the
      // Processing tab ever DOES need state that must outlive the child (e.g. a
      // saved-layout restore, which keep-alive cannot cover), lift it to this
      // host the way AMET0-3522 lifted iceRowData/iceDateRange — see the
      // "State-Lifting Pattern" section of this lib's CLAUDE.md.
      //
      // Leaving ICE drops the ICE detail selection, as it does when switching to
      // tab 1; activeBottomTab is deliberately left untouched because this tab
      // has no bottom tab strip. This branch is purely additive — the tab 1 /
      // tab 2 branches above are unchanged, and their behaviour is re-asserted
      // in corporate-actions-panel-processing.behavior.spec.ts so a regression
      // there is caught by a suite this change set actually runs.
      this.processingTabActivated = true
      this.selectedIceCorporateAction = undefined
    }
  }

  setSelectedIceCorporateAction(action: IceCorporateAction | undefined) {
    this.selectedIceCorporateAction = action
    if (action) {
      this.activeBottomTab = 3
    }
  }

  setIceSelectedClientIds(clientIds: number[]) {
    this.iceSelectedClientIds = clientIds
  }

  setIceDateRange(dateRange: Daterange) {
    this.iceDateRange = dateRange
  }

  setIceRowData(rowData: IceCorporateAction[]) {
    this.iceRowData = rowData
  }
}
